import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const emptyService = {
  id: "",
  service_image: [],
  service_name: "",
  service_link: "",
};

const ServiceModal = ({
  showModal,
  selectedService,
  previewImage,
  handleChange,
  handleImageChange,
  handleSubmit,
  closeModal,
  submitting,
  message,
  error,
  modalMode,
}) => {
  if (!showModal || !selectedService) return null;

  return createPortal(
    <div style={styles.modalOverlay} onClick={closeModal}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {modalMode === "add" ? "Add Service" : "Edit Service"}
          </h3>
          <button type="button" onClick={closeModal} style={styles.closeBtn}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.imageBox}>
            <label style={styles.label}>Service Image</label>

            {previewImage ? (
              <img
                src={previewImage}
                alt="Service Preview"
                style={styles.image}
              />
            ) : (
              <div style={styles.noImageBox}>No image selected</div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Service Name</label>
            <input
              type="text"
              name="service_name"
              value={selectedService.service_name || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter service name"
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Service Link</label>
            <input
              type="text"
              name="service_link"
              value={selectedService.service_link || ""}
              onChange={handleChange}
              style={styles.input}
              placeholder="Enter service link"
            />
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.button} disabled={submitting}>
            {submitting
              ? "Saving..."
              : modalMode === "add"
              ? "Add Service"
              : "Update Service"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
};

const ServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [previewImage, setPreviewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("edit");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        "https://eventscons.com/eventbackendnew/api/service-details"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status && result.data) {
        const serviceData = Array.isArray(result.data)
          ? result.data
          : [result.data];

        setServices(serviceData);
      } else {
        throw new Error(result.message || "Failed to fetch services");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode("add");
    setSelectedService({ ...emptyService });
    setPreviewImage("");
    setMessage("");
    setError("");
    setShowModal(true);
  };

  const openEditModal = (service) => {
    setModalMode("edit");
    setSelectedService({
      ...service,
      service_image: service.service_image || [],
    });

    setPreviewImage(
      Array.isArray(service.service_image)
        ? service.service_image[0] || ""
        : service.service_image || ""
    );

    setMessage("");
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedService(null);
    setPreviewImage("");
    setMessage("");
    setError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setSelectedService((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);

      setSelectedService((prev) => ({
        ...prev,
        service_image: [file],
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const payload = new FormData();

      payload.append("service_name", selectedService.service_name || "");
      payload.append("service_link", selectedService.service_link || "");

      if (
        selectedService.service_image &&
        selectedService.service_image.length > 0 &&
        selectedService.service_image[0] instanceof File
      ) {
        payload.append("service_image", selectedService.service_image[0]);
      }

      let apiUrl = "https://eventscons.com/eventbackendnew/api/service-add";

      if (modalMode === "edit") {
        apiUrl = "https://eventscons.com/eventbackendnew/api/service-update";
        payload.append("id", selectedService.id);
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        body: payload,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.message ||
            (modalMode === "add"
              ? "Service add failed"
              : "Service update failed")
        );
      }

      setMessage(
        result.message ||
          (modalMode === "add"
            ? "Service added successfully"
            : "Service updated successfully")
      );

      await fetchServices();

      setTimeout(() => {
        closeModal();
      }, 1000);
    } catch (err) {
      setError(err.message || "Something went wrong while saving");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p style={styles.loading}>Loading services...</p>;
  if (error && services.length === 0) return <p style={styles.error}>{error}</p>;

  return (
    <>
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.headerRow}>
            <h2 style={styles.heading}>Services List</h2>

            <button
              type="button"
              style={styles.addButton}
              onClick={openAddModal}
            >
              + Add Service
            </button>
          </div>

          {message && <p style={styles.success}>{message}</p>}
          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.grid}>
            {services.map((service) => (
              <div key={service.id} style={styles.card}>
                <div style={styles.cardImageWrap}>
                  <img
                    src={
                      Array.isArray(service.service_image)
                        ? service.service_image[0] || ""
                        : service.service_image || ""
                    }
                    alt={service.service_name}
                    style={styles.cardImage}
                  />

                  <button
                    style={styles.editBtn}
                    onClick={() => openEditModal(service)}
                    type="button"
                    title="Edit Service"
                  >
                    ✏️
                  </button>
                </div>

                <h3 style={styles.cardTitle}>{service.service_name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceModal
        showModal={showModal}
        selectedService={selectedService}
        previewImage={previewImage}
        handleChange={handleChange}
        handleImageChange={handleImageChange}
        handleSubmit={handleSubmit}
        closeModal={closeModal}
        submitting={submitting}
        message={message}
        error={error}
        modalMode={modalMode}
      />
    </>
  );
};

const styles = {
  section: {
    padding: "40px 20px",
    border: "1px solid #eee",
    background: "#fff",
    margin: "30px",
    boxShadow: "0px 10px 25px #dcdcdc",
    borderRadius: "15px",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    marginBottom: "25px",
    flexWrap: "wrap",
  },
  heading: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    textAlign: "left",
  },
  addButton: {
    padding: "12px 18px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
  },
  cardImageWrap: {
    position: "relative",
  },
  cardImage: {
    width: "100%",
    height: "220px",
    objectFit: "cover",
    display: "block",
  },
  cardTitle: {
    padding: "16px",
    fontSize: "20px",
    fontWeight: "700",
    color: "#222",
  },
  editBtn: {
    position: "absolute",
    top: "12px",
    right: "12px",
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "none",
    background: "#9B1321",
    color: "#fff",
    cursor: "pointer",
    fontSize: "18px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 99999,
  },
  modal: {
    position: "relative",
    width: "100%",
    maxWidth: "820px",
    maxHeight: "90vh",
    overflowY: "auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#222",
  },
  closeBtn: {
    background: "transparent",
    border: "none",
    fontSize: "30px",
    cursor: "pointer",
    color: "#444",
    lineHeight: 1,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  imageBox: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  image: {
    width: "100%",
    maxWidth: "100%",
    height: "260px",
    objectFit: "cover",
    borderRadius: "10px",
    border: "1px solid #ddd",
  },
  noImageBox: {
    height: "180px",
    border: "1px dashed #bbb",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#666",
    background: "#fafafa",
    fontWeight: "500",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontWeight: "600",
    color: "#333",
  },
  input: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    width: "100%",
  },
  textarea: {
    padding: "12px 14px",
    borderRadius: "8px",
    border: "1px solid #ccc",
    fontSize: "15px",
    resize: "vertical",
    width: "100%",
  },
  button: {
    padding: "14px 20px",
    background: "#9B1321",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    width: "fit-content",
  },
  success: {
    color: "green",
    fontWeight: "600",
    marginBottom: "16px",
  },
  error: {
    color: "red",
    fontWeight: "600",
    marginBottom: "16px",
  },
  loading: {
    padding: "40px",
    textAlign: "center",
  },
};

export default ServicesEditor;